import type { ReactNode } from 'react';
import { ShellPresentationProvider } from '@/components/context/ShellPresentationContext';
import { useHub } from '@/contexts/HubContext';
import { useHubPathname } from '@/lib/global/hub-path';
import { useDemoContextLabel } from '@/lib/presentation-mode/store';
import { useFinanceBookWriter, useFinanceSession } from './book';
import { financeNavigation } from './navigation';
import { isFinanceLogicalPath } from './paths';
import { financeSessionHeading } from './presentation';
import { financeAccessOf } from './registry';
import { useFinanceNavBadges } from './useFinanceNavBadges';

export function FinanceShellPresentation({ children }: { children: ReactNode }) {
  const onFinance = isFinanceLogicalPath(useHubPathname());
  const session = useFinanceSession();
  const writeBook = useFinanceBookWriter();
  const demoLabel = useDemoContextLabel();
  const { selectedOperator } = useHub();
  const heading = financeSessionHeading(session);
  const bookClientId = session.book.kind === 'client' ? session.book.clientId : null;
  const financeClient = bookClientId ? (session.clients.find((client) => client.id === bookClientId) ?? null) : null;
  const access = financeAccessOf(session);
  const navigation = financeNavigation(access);
  const groups = useFinanceNavBadges(navigation.groups, access, onFinance);
  const scope = {
    title: heading.title,
    subtitle: heading.subtitle,
    label: demoLabel ?? heading.title,
    imageUrl: demoLabel ? null : (financeClient?.logo_url ?? selectedOperator?.avatar_url ?? null),
    operatorAvatar: session.book.kind === 'operator' && !demoLabel,
    selectedClient: financeClient,
    operatorSelected: session.book.kind === 'operator',
    mineSelected: session.book.kind === 'mine',
    operatorOption: session.wildcard
      ? { label: 'All Clients', onSelect: () => writeBook({ kind: 'operator' }, { push: true }) }
      : undefined,
    mineOption:
      !session.wildcard && session.clients.length > 1
        ? { label: 'My clients', onSelect: () => writeBook({ kind: 'mine' }, { push: true }) }
        : undefined,
    showDebtTypes: false,
    onClientSelect: (client: (typeof session.clients)[number]) => writeBook({ kind: 'client', clientId: client.id }, { push: true }),
    onConfirmScope: (client: (typeof session.clients)[number]) => writeBook({ kind: 'client', clientId: client.id }, { push: true }),
  };
  return (
    <ShellPresentationProvider
      value={{
        scope: onFinance ? scope : null,
        navigation: onFinance ? { ...navigation, groups } : null,
        documentTitle: onFinance ? `Ophelos Hub · ${demoLabel ?? heading.title}` : null,
      }}
    >
      {children}
    </ShellPresentationProvider>
  );
}
